import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { S4aHelperModal, isReleased } from '../find/S4aHelperModal';
import type { ReleaseOption } from '../hooks/useReleaseOptions';
import type { CampaignDetail } from '../outreachTypes';
import { monthDay } from './campaignFormat';
import { campaignPageMessages as messages } from './campaignPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the campaign page; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Campaign.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** Spotify takes an editorial pitch until 7 days before the release day. */
const EDITORIAL_DAYS_BEFORE = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * "Spotify editorial" (design outreach-v2 g1 side): Plug cannot pitch Spotify's own playlists, so
 * the pitch helper (prompt P8) opens for an unreleased song. A song that is out shows no card.
 */
const EditorialHelperCardComponent: React.FC<{ accountId: number; campaign: CampaignDetail }> = ({
  accountId,
  campaign,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const song = useMemo<ReleaseOption>(
    () => ({
      catalogItemUuid: campaign.catalogItemUuid,
      title: campaign.songTitle,
      coverUrl: campaign.coverUrl,
      releaseDate: campaign.releaseDate,
      unreleased: !isReleased(campaign.releaseDate),
    }),
    [campaign]
  );
  if (isReleased(campaign.releaseDate)) {
    return null;
  }
  const deadline =
    campaign.releaseDate === null
      ? null
      : new Date(Date.parse(campaign.releaseDate) - EDITORIAL_DAYS_BEFORE * DAY_MS).toISOString();
  return (
    <section className={styles.card} data-testid="editorial-helper-card">
      <h3 className={styles.cardTitle}>{intl.formatMessage(messages.editorialTitle)}</h3>
      <p className={`${shared.small} m-0`}>
        {deadline === null
          ? intl.formatMessage(messages.editorialBodyNoDate)
          : intl.formatMessage(messages.editorialBody, { date: monthDay(intl, deadline) })}
      </p>
      <div>
        <SMLButton size="sm" outline omitCap onClick={() => setIsOpen(true)}>
          {intl.formatMessage(messages.editorialOpen)}
        </SMLButton>
      </div>
      {isOpen && <S4aHelperModal accountId={accountId} song={song} onClose={() => setIsOpen(false)} />}
    </section>
  );
};

export const EditorialHelperCard = TypedMemo(EditorialHelperCardComponent);
